import React, { useState, useRef } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Download, Upload, Loader2, ShieldAlert, CheckCircle2, ArrowLeft, RefreshCw } from "lucide-react";
import { Link, Navigate } from "react-router-dom";

/**
 * /admin/migration — one-shot tool for the admin to move their own profile
 * (user doc + related records + file bytes) between environments. Typical
 * usage: EXPORT from preview → download JSON → sign in on prod → IMPORT.
 * Only visible to admins; server enforces the same via require_admin.
 */
export default function AdminMigration() {
  const { user } = useAuth();
  const [exporting, setExporting] = useState(false);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [stripeSyncing, setStripeSyncing] = useState(false);
  const [stripeResult, setStripeResult] = useState(null);
  const fileRef = useRef(null);

  const syncStripe = async () => {
    setStripeSyncing(true);
    setStripeResult(null);
    try {
      const r = await api.post("/admin/setup-stripe");
      setStripeResult(r.data);
      toast.success("Stripe catalog synced");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Sync failed");
    } finally { setStripeSyncing(false); }
  };

  const [archiving, setArchiving] = useState(false);
  const [archiveResult, setArchiveResult] = useState(null);
  const archiveLegacy = async (dryRun) => {
    if (!dryRun && !window.confirm("Archive every non-canonical PJP product in Stripe? This is safe (managed products are preserved) but archived products stop accepting new checkouts.")) return;
    setArchiving(true);
    setArchiveResult(null);
    try {
      const r = await api.post(`/admin/archive-legacy-stripe-products?dry_run=${dryRun}`);
      setArchiveResult(r.data);
      toast.success(dryRun ? "Preview ready" : `Archived ${r.data.archived}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Archive failed");
    } finally { setArchiving(false); }
  };

  if (user && !user.is_admin) return <Navigate to="/" replace />;

  const exportBundle = async () => {
    setExporting(true);
    try {
      const r = await api.get("/admin/migration/export-me", { responseType: "blob" });
      const blob = new Blob([r.data], { type: "application/json" });
      const filename = `pjp-profile-${(user?.email || "me").replace(/@.*$/, "")}-${new Date().toISOString().slice(0,10)}.json`;
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Bundle downloaded");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Export failed");
    } finally { setExporting(false); }
  };

  const importBundle = async (file) => {
    if (!file) return;
    if (!window.confirm(`This will OVERWRITE the ${user?.email} account on THIS environment (${window.location.hostname}). Continue?`)) {
      if (fileRef.current) fileRef.current.value = "";
      return;
    }
    setImporting(true);
    setImportResult(null);
    try {
      const text = await file.text();
      const bundle = JSON.parse(text);
      const r = await api.post("/admin/migration/import-user", { bundle, overwrite: true });
      setImportResult(r.data);
      toast.success("Import complete");
    } catch (e) {
      toast.error(e?.response?.data?.detail || e?.message || "Import failed");
    } finally {
      setImporting(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  return (
    <div className="min-h-screen bg-obsidian text-white">
      <div className="max-w-3xl mx-auto px-4 py-10">
        <Link to="/dashboard" className="mono text-[10px] tracking-widest uppercase text-cyan/80 hover:text-cyan inline-flex items-center gap-1 mb-6">
          <ArrowLeft className="h-3 w-3" /> Back to cockpit
        </Link>

        <div className="mono text-[10px] tracking-[0.24em] uppercase text-cyan/80">// ADMIN TOOL</div>
        <h1 className="font-display text-3xl md:text-4xl font-bold tracking-tight mt-1">Account Migration</h1>
        <p className="text-white/60 mt-2">Move your full admin account (profile fields, identity, ratings, files, gallery, posts, notifications) between environments.</p>

        <div className="mt-6 rounded-lg border border-yellow-500/40 bg-yellow-500/[0.05] p-4 flex items-start gap-3">
          <ShieldAlert className="h-5 w-5 text-yellow-400 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-yellow-200">
            <div className="font-semibold mb-1">Handle this file carefully</div>
            The exported bundle contains your <em>hashed password</em>, passport image, driver's license image, and verification records. Do not share, do not email it. Only upload it into your own PJP environment.
          </div>
        </div>

        {/* EXPORT */}
        <section className="mt-8 rounded-lg border border-white/10 bg-white/[0.02] p-6" data-testid="migration-export-section">
          <div className="flex items-baseline justify-between mb-3 flex-wrap gap-2">
            <div>
              <div className="mono text-[10px] tracking-widest uppercase text-cyan/80">STEP 1 — EXPORT</div>
              <h2 className="font-display text-xl font-semibold mt-0.5">Download your account bundle</h2>
              <p className="text-white/60 text-sm mt-1">Downloads a single <code className="mono text-cyan">.json</code> file containing everything on this environment for {user?.email}.</p>
            </div>
          </div>
          <button
            type="button"
            onClick={exportBundle}
            disabled={exporting}
            data-testid="btn-migration-export"
            className="mono text-[11px] tracking-widest uppercase px-4 py-2.5 rounded bg-cyan text-obsidian font-bold hover:brightness-110 disabled:opacity-40 inline-flex items-center gap-1.5"
          >
            {exporting ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Download className="h-3.5 w-3.5" />}
            {exporting ? "Preparing…" : "Download Bundle"}
          </button>
        </section>

        {/* IMPORT */}
        <section className="mt-6 rounded-lg border border-white/10 bg-white/[0.02] p-6" data-testid="migration-import-section">
          <div className="flex items-baseline justify-between mb-3 flex-wrap gap-2">
            <div>
              <div className="mono text-[10px] tracking-widest uppercase text-cyan/80">STEP 2 — IMPORT</div>
              <h2 className="font-display text-xl font-semibold mt-0.5">Restore into this environment</h2>
              <p className="text-white/60 text-sm mt-1">Uploads a bundle exported from another environment. <strong className="text-yellow-300">Overwrites your account on this environment.</strong></p>
            </div>
          </div>
          <input
            ref={fileRef}
            type="file"
            accept="application/json,.json"
            className="hidden"
            onChange={(e) => importBundle(e.target.files?.[0])}
            data-testid="input-migration-bundle"
          />
          <button
            type="button"
            onClick={() => fileRef.current?.click()}
            disabled={importing}
            data-testid="btn-migration-import"
            className="mono text-[11px] tracking-widest uppercase px-4 py-2.5 rounded border border-cyan/50 bg-cyan/[0.08] text-cyan font-bold hover:bg-cyan/[0.16] disabled:opacity-40 inline-flex items-center gap-1.5"
          >
            {importing ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Upload className="h-3.5 w-3.5" />}
            {importing ? "Importing…" : "Choose Bundle File"}
          </button>

          {importResult && (
            <div className="mt-5 rounded border border-avail/40 bg-avail/[0.06] p-4" data-testid="migration-import-result">
              <div className="flex items-center gap-2 text-avail mb-2">
                <CheckCircle2 className="h-4 w-4" />
                <span className="mono text-[11px] tracking-widest uppercase font-bold">Import complete</span>
              </div>
              <div className="text-sm text-white/85">User ID: <span className="mono text-cyan">{importResult.user_id}</span></div>
              <div className="text-sm text-white/85 mt-1">
                Files written: <span className="mono">{importResult.stats?.files_written ?? 0}</span>
                {importResult.stats?.files_missing_bytes ? <> · missing bytes: <span className="mono text-yellow-400">{importResult.stats.files_missing_bytes}</span></> : null}
              </div>
              {importResult.stats?.collections && Object.keys(importResult.stats.collections).length > 0 && (
                <details className="mt-3">
                  <summary className="mono text-[10px] tracking-widest uppercase text-white/60 cursor-pointer">Collection stats</summary>
                  <pre className="mt-2 text-[11px] text-white/80 bg-black/40 rounded p-2 overflow-x-auto">{JSON.stringify(importResult.stats.collections, null, 2)}</pre>
                </details>
              )}
              <div className="mt-3 text-xs text-white/60">Reload the app to see your restored profile.</div>
            </div>
          )}
        </section>

        {/* STRIPE CATALOG SYNC — separate from account migration but useful
            for admins after any pricing change */}
        <section className="mt-6 rounded-lg border border-white/10 bg-white/[0.02] p-6" data-testid="stripe-sync-section">
          <div className="flex items-baseline justify-between mb-3 flex-wrap gap-2">
            <div>
              <div className="mono text-[10px] tracking-widest uppercase text-cyan/80">STRIPE CATALOG</div>
              <h2 className="font-display text-xl font-semibold mt-0.5">Sync live pricing to Stripe</h2>
              <p className="text-white/60 text-sm mt-1">Runs the setup_stripe catalog against your current Stripe account (uses <code className="mono text-cyan">STRIPE_SECRET_KEY</code> from server env). Idempotent — existing subscribers keep their price; only new checkouts use updated intervals.</p>
            </div>
          </div>
          <button
            type="button"
            onClick={syncStripe}
            disabled={stripeSyncing}
            data-testid="btn-sync-stripe"
            className="mono text-[11px] tracking-widest uppercase px-4 py-2.5 rounded bg-cyan text-obsidian font-bold hover:brightness-110 disabled:opacity-40 inline-flex items-center gap-1.5"
          >
            {stripeSyncing ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />}
            {stripeSyncing ? "Syncing…" : "Sync Stripe Catalog"}
          </button>

          {stripeResult && (
            <div className="mt-5 rounded border border-avail/40 bg-avail/[0.06] p-3" data-testid="stripe-sync-result">
              <div className="flex items-center gap-2 text-avail mb-2">
                <CheckCircle2 className="h-4 w-4" />
                <span className="mono text-[11px] tracking-widest uppercase font-bold">Catalog synced</span>
              </div>
              <div className="mt-2 overflow-x-auto">
                <table className="w-full text-[12px]">
                  <thead>
                    <tr className="text-left mono text-[10px] tracking-widest uppercase text-white/50">
                      <th className="py-1 pr-3">Product</th><th className="py-1 pr-3">Lookup key</th><th className="py-1 pr-3">Amount</th><th className="py-1">Interval</th>
                    </tr>
                  </thead>
                  <tbody className="mono">
                    {(stripeResult.results || []).map((r, i) => (
                      <tr key={i} className={r.error ? "text-red-400" : "text-white/85"}>
                        <td className="py-0.5 pr-3">{r.product}</td>
                        <td className="py-0.5 pr-3">{r.lookup_key || "—"}</td>
                        <td className="py-0.5 pr-3">{r.amount != null ? `$${(r.amount/100).toFixed(2)}` : "—"}</td>
                        <td className="py-0.5">{r.error ? `ERR: ${r.error.slice(0,60)}` : r.interval}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* CLEANUP — archive legacy/duplicate PJP products in Stripe */}
          <div className="mt-5 pt-5 border-t border-white/[0.06]">
            <div className="mono text-[10px] tracking-widest uppercase text-yellow-400/80 mb-1">CLEANUP · LEGACY / DUPES</div>
            <div className="text-white/60 text-xs mb-3">
              Archives every active PJP product in Stripe that isn't in the current CATALOG (safe — managed products always preserved). Use this once after renaming/reorganizing products. Always dry-run first to preview what will be archived.
            </div>
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                onClick={() => archiveLegacy(true)}
                disabled={archiving}
                data-testid="btn-archive-dry-run"
                className="mono text-[10px] tracking-widest uppercase px-3 py-2 rounded border border-white/20 text-white/80 hover:bg-white/5 disabled:opacity-40 inline-flex items-center gap-1.5"
              >
                {archiving ? <Loader2 className="h-3 w-3 animate-spin" /> : <RefreshCw className="h-3 w-3" />}
                Preview archive (dry-run)
              </button>
              <button
                type="button"
                onClick={() => archiveLegacy(false)}
                disabled={archiving || !archiveResult}
                data-testid="btn-archive-execute"
                className="mono text-[10px] tracking-widest uppercase px-3 py-2 rounded border border-red-400/40 bg-red-400/[0.08] text-red-300 hover:bg-red-400/[0.16] disabled:opacity-40 inline-flex items-center gap-1.5"
              >
                {archiving ? <Loader2 className="h-3 w-3 animate-spin" /> : <ShieldAlert className="h-3 w-3" />}
                Archive for real
              </button>
            </div>
            {archiveResult && (
              <div className="mt-3 rounded border border-white/10 bg-black/30 p-3 text-[12px]" data-testid="archive-result">
                <div className="text-white/70">
                  Scanned <span className="mono text-cyan">{archiveResult.scanned}</span> · Managed (kept) <span className="mono text-avail">{archiveResult.kept_count}</span> · To archive <span className="mono text-yellow-400">{archiveResult.archive_candidates?.length || 0}</span> · Archived <span className="mono text-red-400">{archiveResult.archived}</span>
                </div>
                {archiveResult.archive_candidates?.length > 0 && (
                  <details className="mt-2">
                    <summary className="mono text-[10px] tracking-widest uppercase text-white/50 cursor-pointer">Show archive candidates ({archiveResult.archive_candidates.length})</summary>
                    <ul className="mt-2 space-y-0.5 max-h-56 overflow-y-auto">
                      {archiveResult.archive_candidates.map((c) => (
                        <li key={c.id} className="mono text-[11px] text-white/70 truncate">— {c.name} <span className="text-white/30">({c.id})</span></li>
                      ))}
                    </ul>
                  </details>
                )}
              </div>
            )}
          </div>
        </section>

        <div className="mt-6 text-xs text-white/50">
          Migration between preview and production is a one-shot: export on the source, import on the destination. If you re-export later, re-importing will replace all local data with the source again.
        </div>
      </div>
    </div>
  );
}
